import { Box, Card, Flex, HStack, Image, Text } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { AttributeSelect, AttributeSelectOption } from "@keita/lanepatrol-sdk";
import { AttributeUtils } from "../../../../../../coding/utils/attribute-utils";
import {
  getAttributeValue,
  parseAttributeInputValue,
} from "../../../../../../coding/utils/attribute-value";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { useAttributeHighlight } from "../../hooks/useAttributeHighlight";
import { AttributeWrapper } from "./AttributeWrapper";
import { DotIcon } from "../../../../../../shared/views/icons";
import { OsaNodeCoding } from "../../../../../../coding/models/OsaNodeCoding";

const ImageSelectAttributeBase = ({
  attributeDefinition,
  disabled,
  optional,
  nodeCoding,
  compact,
}: {
  attributeDefinition: AttributeSelect;
  disabled: boolean;
  optional: boolean;
  compact: boolean;
  nodeCoding: OsaNodeCoding;
}) => {
  const { codingStore } = useStores();
  const highlighted = useAttributeHighlight(attributeDefinition);

  if (disabled) return null;

  const isSuggested = nodeCoding.suggestions.includes(attributeDefinition.name);

  const attributeValue = getAttributeValue(
    attributeDefinition.name,
    nodeCoding.attributes,
  );
  const selectedOption = attributeDefinition.options.find(
    (option) => option.value === attributeValue,
  );

  const onSelect = (option: AttributeSelectOption, ctrlKey: boolean) => {
    const parsedValue = parseAttributeInputValue(
      attributeDefinition,
      ctrlKey ? null : option.value,
    );

    codingStore.codeAttribute(attributeDefinition.name, parsedValue);
  };

  const getColor = (isSelected: boolean) =>
    isSelected && isSuggested
      ? `gray.${highlighted ? 300 : 200}`
      : isSelected
      ? `primary.${highlighted ? 300 : 100}`
      : "transparent";

  return (
    <AttributeWrapper
      label={AttributeUtils.getLabel(attributeDefinition.name)}
      attributeDefinition={attributeDefinition}
      isSuggested={isSuggested}
      compact={compact}
    >
      <Card.Root
        variant="outline"
        borderColor={
          attributeValue == null && !optional ? "red.400" : undefined
        }
      >
        {compact ? (
          <HStack flexWrap="wrap" gap={0} separator={<DotIcon />}>
            {attributeDefinition.options?.map((option) => {
              const isSelected = option === selectedOption;
              return (
                <Box
                  p={1}
                  borderRadius="4px"
                  key={option.value}
                  transitionDuration="0.2s"
                  bgColor={getColor(isSelected)}
                  cursor={"pointer"}
                  onClick={(e) => onSelect(option, e.ctrlKey || e.metaKey)}
                >
                  <Text fontSize={"xs"}>
                    {AttributeUtils.getOptionLabel(option.value)}
                  </Text>
                </Box>
              );
            })}
          </HStack>
        ) : (
          <HStack flexWrap="wrap" flexGrow={1} justify="center" gap={0}>
            {attributeDefinition.options?.map((option) => {
              const isSelected = option === selectedOption;
              return (
                <Flex
                  overflow="hidden"
                  flexShrink={0}
                  borderRadius="4px"
                  w={"80px"}
                  maxW="80px"
                  justifyContent="center"
                  key={option.value}
                  transitionDuration="0.2s"
                  bgColor={getColor(isSelected)}
                  cursor={"pointer"}
                  onClick={(e) => onSelect(option, e.ctrlKey || e.metaKey)}
                >
                  <Box m={-2}>
                    <Image
                      title={AttributeUtils.getOptionLabel(option.value)}
                      src={AttributeUtils.getImage(
                        attributeDefinition,
                        option.value,
                      )}
                    />
                  </Box>
                </Flex>
              );
            })}
          </HStack>
        )}
      </Card.Root>
    </AttributeWrapper>
  );
};
export const ImageSelectAttribute = observer(ImageSelectAttributeBase);
